import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    InjectionToken,
    Injector,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { DataFieldEditBase } from '../data-field-edit-base';
import { DropdownSettingsModelV2 } from '@fi-shared/dropdown';
import { propComparator } from '@fi-shared/utils/array-sort-comparators';
import { ILightWeightBusinessUnit, ILightWeightObject } from '@fi-shared/models';
import { BusinessUnitModel } from '@fi-app/business-unit/business-unit.model';
import { takeUntil } from 'rxjs/operators';
import { FiDropdownItemAdditionalTextFn, FiDropdownItemDisabledFn } from '@fi-shared/fi-select-search/fi-select-search';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { AppTextService } from '@fi-shared/app-text';

export type TDataFieldEditUnitsComponent_Settings = {
    lightWeightBusinessUnits: ILightWeightObject[];
    AccessRightFilter: number[];
    dataFieldItemProperty: string;
    dataFieldItemPropertyPreferred?: string;
    showSelectAll: boolean;
    isSingleSelect?: boolean;
    onBusinessUnitsChange: () => void;
    isDisabledFunc: (businessUnit: ILightWeightObject) => boolean;
    isChipsSearchLayout?: boolean;
    isDeactivatedLabelShown?: boolean;
};

export const DataFieldEditUnitsComponent_Settings = new InjectionToken<TDataFieldEditUnitsComponent_Settings>(
    'DataFieldEditUnitsComponent_Settings',
);

@Component({
    selector: 'data-field-edit-units',
    templateUrl: './units.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'data-field-edits-units' },
    standalone: false,
})
export class DataFieldEditUnitsComponent extends DataFieldEditBase implements OnInit {
    businessUnitsListData = new DropdownSettingsModelV2<ILightWeightObject>({
        filterBy: 'Name',
    });
    businessUnitsPreferredListData = new DropdownSettingsModelV2<BusinessUnitModel>({
        filterBy: 'Name',
    });

    constructor(
        injector: Injector,
        @Inject(DataFieldEditUnitsComponent_Settings) public settings: TDataFieldEditUnitsComponent_Settings,
        private userDataService: FiUserDataService,
        private cdr: ChangeDetectorRef,
        private appTextService: AppTextService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.setBusinessUnitsListsData();
        this.onBusinessUnitsChange();
    }

    private setBusinessUnitsListsData() {
        this.businessUnitsListData.setDataSet(
            this.settings.lightWeightBusinessUnits.sort(propComparator(this.businessUnitsListData.filterBy)),
        );
        this.businessUnitsPreferredListData.dataSet$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.cdr.detectChanges());
    }

    isSelectBusinessUnitsDisabledFn: FiDropdownItemDisabledFn<ILightWeightObject> = (
        item: ILightWeightObject,
        allItems: ILightWeightObject[],
    ) => {
        // todo union with DataFieldEditUnitsComponent_Settings
        if (this.isDisabled(item)) return true;

        const availableBusinessUnitIds: number[] = this.settings.AccessRightFilter;
        return availableBusinessUnitIds?.length && availableBusinessUnitIds.indexOf(item.Id) === -1;
    };

    isSelectCurrentBusinessUnitDisabledFn: FiDropdownItemDisabledFn<ILightWeightObject> = (
        item: ILightWeightObject,
        allItems: ILightWeightObject[],
    ) => {
        // todo union with DataFieldEditUnitsComponent_Settings
        const isProfileMode = this.dataFieldObject.Id === this.userDataService.getUserId();
        if (isProfileMode) {
            // allow change preferred business unit for user their self
            return false;
        }

        const availableBusinessUnitIds: number[] = this.settings.AccessRightFilter;
        return availableBusinessUnitIds?.length && availableBusinessUnitIds.indexOf(item.Id) === -1;
    };

    isBusinessUnitDeactivatedFn: FiDropdownItemDisabledFn<ILightWeightObject> = (
        item: ILightWeightObject,
        allItems: ILightWeightObject[],
    ) => {
        return this.settings.isDeactivatedLabelShown && !item.IsActivated;
    };

    getOptionAdditionalTextFn: FiDropdownItemAdditionalTextFn<ILightWeightBusinessUnit> = (
        item: ILightWeightBusinessUnit,
        allItems: ILightWeightBusinessUnit[],
    ): string => {
        if (!item.IsActivated) {
            return this.appTextService.get('common.deactivated');
        }
    };

    getOptionAdditionalClassFn: FiDropdownItemAdditionalTextFn<ILightWeightBusinessUnit> = (
        item: ILightWeightBusinessUnit,
        allItems: ILightWeightBusinessUnit[],
    ): string => {
        if (!item.IsActivated) {
            return 'red-color';
        }
    };

    onBusinessUnitsChange() {
        if (this.settings.dataFieldItemPropertyPreferred)
            this.businessUnitsPreferredListData.setDataSet(this.getLightWeightUserBusinessUnits());

        this.settings.onBusinessUnitsChange();
    }

    getPreferredBusinessUnit(): BusinessUnitModel {
        return this.getLightWeightUserBusinessUnits().find((unit: BusinessUnitModel) => {
            return (
                unit[this.businessUnitsPreferredListData.dataValueField] ===
                this.dataFieldObject[this.settings.dataFieldItemPropertyPreferred]
            );
        });
    }

    getUnitSelectionText(): string {
        return this.getPreferredBusinessUnit()?.Name || '';
    }

    protected getLightWeightUserBusinessUnits(): BusinessUnitModel[] {
        return this.dataFieldObject[this.settings.dataFieldItemProperty].map((selectedUserToBusinessUnitId: number) => {
            const businessUnit = new BusinessUnitModel();
            businessUnit.Id = selectedUserToBusinessUnitId;
            const businessUnitObject = this.businessUnitsListData.dataSet.find((businessUnit: ILightWeightObject) => {
                return businessUnit[this.businessUnitsListData.dataValueField] === selectedUserToBusinessUnitId;
            });
            businessUnit.Name = businessUnitObject.Name;
            businessUnit.IsSystemObject = businessUnitObject.IsSystemObject;
            businessUnit.IsActivated = businessUnitObject.IsActivated;
            return businessUnit;
        });
    }

    isDisabled(businessUnit?: ILightWeightObject): boolean {
        if (this.settings && this.settings.isDisabledFunc) return this.settings.isDisabledFunc(businessUnit);
        return false;
    }

    isChipsSearchLayout(): boolean {
        return this.settings?.isChipsSearchLayout;
    }
}
